import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { DrawdownAnnualIncome } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownAnnualIncome";
import DifferentInitialIncomePayment from "./DifferentInitialIncomePayment";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { processTypeEnum } from "../../Resource/Contents";
import { PensionTransfer } from "../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import {
    calculateMaximumAvailablePerPayment,
    calculateMaximumIncomePerPaymentWithStandardAlgorithm,
    calculateMaximumIncomePerPaymentWithTargetAlgorithm,
    getInitialPaymentRequested,
    shouldDisplayDifferentInitialIncomePayment,
} from "./Helper/IncomePaymentHelper";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { InvalidateDate } from "../../../../../../../react-core/Datepicker/Resources/ContentContent";
import { addYears, stripToDay } from "../../../../../../../react-core/utils/date";
import { DrawdownIncomeAmendmentInstruction } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeAmendmentInstruction";

interface IIncomeRequiredProps extends IProcessComponentProps {
    data: DrawdownAnnualIncome;
    onChange: (changes) => void;
    labels: any;
    messages: any;
    drawdownType: DrawdownType;
    pensionTransfer?: PensionTransfer[];
    regularCrystalDifferentInital?: boolean;
    processType?: processTypeEnum;
    hasCappedArrangement?: boolean;
    pensionBase: PensionBase;
    isITFC: boolean;
    existingArrangements?: DrawdownIncomeAmendmentInstruction[];
}
interface IIncomeRequiredState {}

export class IncomeRequired extends ProcessComponent<IIncomeRequiredProps, IIncomeRequiredState> {
    constructor(props: any) {
        super(props);
    }

    componentDidMount() {
        const { data, onChange, drawdownType, regularCrystalDifferentInital } = this.props;
        super.componentDidMount();
        if (drawdownType === DrawdownType.Regular && regularCrystalDifferentInital) {
            onChange({ TakeDifferentInitialIncomePayment: true });
        }
        if (drawdownType !== DrawdownType.Regular && data?.TargetSpecificArrangements != true) {
            onChange({ TargetSpecificArrangements: false });
        }
    }

    private caluclateMaximumIncomePaymentByStandard = (
        processType: processTypeEnum,
        pensionBase: PensionBase,
        isITFC: boolean,
        existingArrangements: DrawdownIncomeAmendmentInstruction[]
    ) => {
        const returnValue =
            calculateMaximumIncomePerPaymentWithStandardAlgorithm(
                processType,
                pensionBase,
                isITFC,
                existingArrangements
            ) - (getInitialPaymentRequested(pensionBase) || 0);
        return returnValue < 0 ? 0 : returnValue;
    };

    private caluclateMaximumIncomePaymentByTarget = (
        processType: processTypeEnum,
        pensionBase: PensionBase,
        isITFC: boolean,
        existingArrangements: DrawdownIncomeAmendmentInstruction[]
    ) => {
        const returnValue =
            calculateMaximumIncomePerPaymentWithTargetAlgorithm(
                processType,
                pensionBase,
                isITFC,
                existingArrangements
            ) - (getInitialPaymentRequested(pensionBase) || 0);
        return returnValue < 0 ? 0 : returnValue;
    };
    yearFromNow = () => {
        const result = stripToDay(addYears(new Date(), 1));
        if (result.getDate() > 28) {
            result.setDate(28);
        }
        return result;
    };
    public render(): JSX.Element {
        const {
            data,
            labels,
            onChange,
            messages,
            drawdownType,
            pensionTransfer,
            processType,
            hasCappedArrangement,
            regularCrystalDifferentInital,
            pensionBase,
            isITFC,
            existingArrangements,
        } = this.props;
        return (
            <>
                {drawdownType !== DrawdownType.Regular && (
                    <RadiosEditor
                        cssWrapper="rg-inline mb-16"
                        label={labels.targetSpecificArrangements}
                        selected={data?.TargetSpecificArrangements || false}
                        options={[true, false]}
                        getText={(v) => (v ? "Yes" : "No")}
                        onChange={(v) => {
                            if (v) {
                                onChange({
                                    TargetSpecificArrangements: v,
                                    TakeDifferentInitialIncomePayment: false,
                                    GrossInitialIncomePayment: {
                                        Amount: 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPayment: {
                                        Amount: 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    FlexiIncomeReviewDate:
                                        data?.FlexiIncomeReviewDate &&
                                        data?.FlexiIncomeReviewDate.toString() != InvalidateDate
                                            ? data?.FlexiIncomeReviewDate
                                            : this.yearFromNow(),
                                    FrequencyFlexiIncomeReview:
                                        data?.FrequencyFlexiIncomeReview && data?.FrequencyFlexiIncomeReview != 0
                                            ? data?.FrequencyFlexiIncomeReview
                                            : 12,
                                });
                            } else {
                                onChange({
                                    TargetSpecificArrangements: v,
                                    GrossInitialIncomePayment: {
                                        Amount: 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPayment: {
                                        Amount: 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    GrossIncomePerPaymentFromPension: {
                                        Amount: 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    FlexiIncomeReviewDate: null,
                                    FrequencyFlexiIncomeReview: null,
                                });
                            }
                        }}
                    />
                )}
                {data?.TargetSpecificArrangements && processType != processTypeEnum.topUpWithITFLS && (
                    <div className="row" data-test="targetSpecificArrangementsMessage">
                        {messages.targetSpecificArrangementsMessage}
                    </div>
                )}
                {!data?.TargetSpecificArrangements && drawdownType === DrawdownType.Capped && (
                    <Alert
                        message={messages.standardAlgorithmCappedMessage}
                        alertType={AlertType.Warning}
                        data-test="standardAlgorithmCappedMessage"
                    />
                )}
                {pensionTransfer && pensionTransfer.find((transfer) => transfer.IsInheritedFund) && (
                    <Alert
                        message={messages.inheritedFundMessag}
                        alertType={AlertType.Warning}
                        data-test="inheritedFundMessage"
                    />
                )}
                {shouldDisplayDifferentInitialIncomePayment(
                    data,
                    drawdownType,
                    pensionTransfer,
                    hasCappedArrangement,
                    processType,
                    regularCrystalDifferentInital
                ) && (
                    <DifferentInitialIncomePayment
                        data={data}
                        onChange={(v) => {
                            onChange(v);
                        }}
                        labels={labels}
                        messages={messages}
                        drawdownType={drawdownType}
                        onRef={(ref) => this.appendChild(ref)}
                        pensionBase={pensionBase}
                        processType={processType}
                        isITFC={isITFC}
                        existingArrangements={existingArrangements}
                        regularCrystalDifferentInital={regularCrystalDifferentInital}
                    />
                )}
                {drawdownType === DrawdownType.Regular ? (
                    <div className="row">
                        <span className="col-6 h4">{labels.maximumAvailablePerPayment}</span>
                        <span className="col-6 pl-0" data-test="maximumAvailablePerPayment">
                            {MoneyDisplayHelper.FromatMoneyDisplay(
                                calculateMaximumAvailablePerPayment(processType, pensionBase?.DrawdownModel),
                                CurrencyCode.GBP
                            )}
                        </span>
                    </div>
                ) : (
                    <div className="row">
                        <span className="col-6 h4">{labels.maximumIncomePerPayment}</span>
                        <span className="col-6 pl-0" data-test="maximumIncomePerPayment">
                            {data?.TargetSpecificArrangements !== true
                                ? MoneyDisplayHelper.FromatMoneyDisplay(
                                      this.caluclateMaximumIncomePaymentByStandard(
                                          processType,
                                          pensionBase,
                                          isITFC,
                                          existingArrangements
                                      ),
                                      CurrencyCode.GBP
                                  )
                                : MoneyDisplayHelper.FromatMoneyDisplay(
                                      this.caluclateMaximumIncomePaymentByTarget(
                                          processType,
                                          pensionBase,
                                          isITFC,
                                          existingArrangements
                                      ),
                                      CurrencyCode.GBP
                                  )}
                        </span>
                    </div>
                )}
                {!data?.TargetSpecificArrangements && (
                    <div className="row">
                        <div className="col-4 ">
                            <MoneyTextBoxEditor
                                label={labels.grossIncomePerPayment}
                                value={data?.GrossIncomePerPayment?.Amount}
                                currency={data?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP}
                                onChange={(v) => {
                                    onChange({
                                        GrossIncomePerPayment: {
                                            Amount: v,
                                            Currency: data?.GrossIncomePerPayment?.Currency || CurrencyCode.GBP,
                                        },
                                    });
                                }}
                                required={messages.grossIncomePerPaymentRequired}
                                validationRules={[
                                    {
                                        errorMessage: "Please enter an amount that is greater than zero.",
                                        validator: (v) => (drawdownType === DrawdownType.Flexible ? v >= 0 : v > 0),
                                    },
                                ]}
                                atMost={{
                                    errorMessage:
                                        drawdownType === DrawdownType.Regular
                                            ? messages.grossAvailablePerPaymentOver
                                            : messages.grossIncomePerPaymentOver,
                                    value:
                                        drawdownType === DrawdownType.Regular
                                            ? calculateMaximumAvailablePerPayment(
                                                  processType,
                                                  pensionBase?.DrawdownModel
                                              ) || 0
                                            : this.caluclateMaximumIncomePaymentByStandard(
                                                  processType,
                                                  pensionBase,
                                                  isITFC,
                                                  existingArrangements
                                              ),
                                }}
                            />
                        </div>
                    </div>
                )}

                {data?.TargetSpecificArrangements && (
                    <div className="row">
                        <div className="col-4 ">
                            <MoneyTextBoxEditor
                                label={labels.grossIncomePerPaymentFromPension}
                                value={data?.GrossIncomePerPaymentFromPension?.Amount}
                                currency={data?.GrossIncomePerPaymentFromPension?.Currency || CurrencyCode.GBP}
                                validationRules={[
                                    {
                                        errorMessage: "Please enter an amount that is greater than zero.",
                                        validator: (v) => (drawdownType === DrawdownType.Flexible ? v >= 0 : v > 0),
                                    },
                                ]}
                                onChange={(v) => {
                                    onChange({
                                        GrossIncomePerPaymentFromPension: {
                                            Amount: v,
                                            Currency:
                                                data?.GrossIncomePerPaymentFromPension?.Currency || CurrencyCode.GBP,
                                        },
                                    });
                                }}
                                required={messages.grossIncomePerPaymentFromPensionRequired}
                                atMost={{
                                    errorMessage: messages.grossIncomePerPaymentOver,
                                    value: this.caluclateMaximumIncomePaymentByTarget(
                                        processType,
                                        pensionBase,
                                        isITFC,
                                        existingArrangements
                                    ),
                                }}
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }
}

export default IncomeRequired;
